import axios, { AxiosInstance } from "axios";
import { API_BASE_URL } from "../config";

axios.defaults.withCredentials = true;

let csrfPromise: Promise<void> | null = null;

/**
 * Safely extract the value portion from a raw "name=value" cookie string.
 * Uses indexOf+slice instead of split("=")[1] so base64-encoded tokens that
 * contain embedded "=" characters are not silently truncated.
 */
function cookieValue(raw: string): string {
  const eq = raw.indexOf("=");
  return eq === -1 ? "" : raw.slice(eq + 1).trim();
}

export function getCSRFToken(): string | null {
  const matches = document.cookie.match(/apicsrftoken=[^;]+/g);
  if (!matches) return null;

  if (matches.length > 1) {
    // Capture the winning token BEFORE clearing duplicates so that any
    // concurrent request racing through ensureCSRF() doesn't see an empty
    // jar and fire a redundant GET /api/csrf/ that would create yet another
    // duplicate and restart the loop.
    const token = cookieValue(matches[matches.length - 1]);

    const paths = ["/", "/api/", "/api"];
    matches.forEach((c) => {
      const name = c.slice(0, c.indexOf("="));
      paths.forEach((p) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${p}`;
      });
    });

    // Re-set the single canonical token on "/" so the next synchronous call
    // to getCSRFToken() finds exactly one match and does not trigger another
    // ensureCSRF() fetch.
    if (token) {
      document.cookie = `apicsrftoken=${token};path=/;SameSite=Strict`;
    }

    return token || null;
  }

  return cookieValue(matches[0]) || null;
}

export async function ensureCSRF() {
  const token = getCSRFToken();
  if (token === null) {
    if (csrfPromise) return csrfPromise;
    csrfPromise = axios
      .get(`${API_BASE_URL}/api/csrf/`, { withCredentials: true })
      .then(() => {}) // Ensure Promise<void>
      .catch((e) => console.warn("Failed to refresh CSRF", e))
      .finally(() => {
        csrfPromise = null;
      });
    await csrfPromise;
  }
}

/**
 * Attaches CSRF interceptors to an axios instance.
 * Follows LOS module pattern for robust session handling.
 */
export function attachCSRFInterceptors(instance: AxiosInstance) {
    // Request Interceptor
    instance.interceptors.request.use(async (config) => {
        if (config.url?.includes("/api/csrf/")) return config;

        await ensureCSRF();
        const token = getCSRFToken();
        if (token) {
            config.headers["X-CSRFToken"] = token;
        }
        return config;
    });

    // Response Interceptor for 403 retries
    instance.interceptors.response.use(
        (response) => {
            if (import.meta.env.DEV) {
                console.log(`✅ ${response.status} ${response.config.url}`);
            }
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            if (
                error.response?.status === 403 &&
                !originalRequest._retry &&
                !originalRequest.url?.includes("/api/csrf/")
            ) {
                originalRequest._retry = true;
                try {
                    await axios.get(`${API_BASE_URL}/api/csrf/`, { withCredentials: true });
                    const token = getCSRFToken();
                    if (token) {
                        originalRequest.headers["X-CSRFToken"] = token;
                    }
                    return axios(originalRequest);
                } catch (e) {
                    return Promise.reject(e);
                }
            }

            return Promise.reject(error);
        }
    );
}

// Initialize default instance
attachCSRFInterceptors(axios);

export default axios;
